import React, { useMemo, useState, useEffect } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import { useContentList } from "../hooks/useContent";
import { useQuery } from "@tanstack/react-query";
import { getCategories } from "../../../api/courses";
import { CourseLevel } from "../../../core/common/data/json/courses";
import { Pagination } from "antd";
import { calculateDiscount, rupiahFormatter } from "../../../utils/helpers";
import { getAdminFavoriteOverride } from "../../../utils/favoriteStore";

const isItemFavorite = (item: any) => {
  if (!item) return false;
  const override = getAdminFavoriteOverride(item?.id);
  if (override !== null) {
    return override;
  }
  return Boolean(
    item?.is_favorite === true ||
    item?.is_favorite === 1 ||
    item?.is_favorite === "1" ||
    item?.is_favorite === "true" ||
    item?.is_favourite === true ||
    item?.is_favourite === 1 ||
    item?.is_favourite === "1" ||
    item?.is_bestseller === true ||
    item?.is_bestseller === 1 ||
    item?.is_bestseller === "1" ||
    item?.is_best_seller === true ||
    item?.is_best_seller === 1 ||
    item?.is_best_seller === "1" ||
    item?.is_featured === true ||
    item?.is_featured === 1 ||
    item?.is_featured === "1" ||
    item?.is_top === true ||
    item?.is_top === 1 ||
    item?.is_top === "1" ||
    item?.is_popular === true ||
    item?.is_popular === 1 ||
    item?.is_popular === "1" ||
    item?.favorite === true ||
    item?.favorite === 1 ||
    item?.favorite === "1" ||
    item?.bestseller === true ||
    item?.bestseller === 1 ||
    item?.bestseller === "1" ||
    item?.badge === "bestseller" ||
    item?.badge === "favorite"
  );
};

const CourseGrid = ({ type }: { type?: string }) => {
  const navigate = useNavigate();

  const {
    content,
    isLoading,
    isRefetching,
    isError,
    error,
    currentPageData,
    total,
    selectedCategories,
    selectedLevels,
    limit,
    isBestSeller: isFavorit,
    sortWaktu,
    searchTerm,
    handleSearchInputChange,
    handleSetBestSeller,
    handleSetSortWaktu,
    onPaginationChange,
    clearAllFilters,
    getCategorySlug,
  } = useContentList({ type, enableUrlParams: true });

  const [searchInputVal, setSearchInputVal] = useState(searchTerm || "");

  useEffect(() => {
    setSearchInputVal(searchTerm || "");
  }, [searchTerm]);

  // Tentukan tipe API untuk query kategori
  const apiType =
    type === "Course"
      ? "course"
      : type === "Live_Teaching"
        ? "live_teaching"
        : type === "CBT"
          ? "cbt"
          : undefined;

  // Fetch kategori untuk combobox dan getBreadcrumbTitle
  const { data: categoriesQueryData } = useQuery({
    queryKey: ["categories", { page: 1, limit: 100, type: apiType }],
    queryFn: () => getCategories({ page: 1, limit: 100, type: apiType }, false),
    refetchOnWindowFocus: false,
  });

  const dataCategories = categoriesQueryData?.data;

  const [kategoriSearch, setKategoriSearch] = useState("");

  useEffect(() => {
    if (selectedCategories.length === 0) {
      setKategoriSearch("");
    } else if (dataCategories && selectedCategories.length > 0) {
      const activeCat = dataCategories.find(
        (cat: any) => cat.id?.toString() === selectedCategories[0],
      );
      if (activeCat) {
        setKategoriSearch(activeCat.name);
      }
    }
  }, [selectedCategories, dataCategories]);

  const displayedContent = content;

  // Countdown state untuk setiap item
  const [countdowns, setCountdowns] = useState<{
    [key: string]: {
      days: number;
      hours: number;
      minutes: number;
      seconds: number;
    };
  }>({});

  // Countdown effect untuk semua item
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const newCountdowns: {
        [key: string]: {
          days: number;
          hours: number;
          minutes: number;
          seconds: number;
        };
      } = {};

      // Hitung countdown untuk setiap item Live_Teaching
      content.forEach((item: any) => {
        if (item.start_date && type === "Live_Teaching") {
          const startDate = new Date(item.start_date).getTime();
          const timeLeft = startDate - now;

          if (timeLeft > 0) {
            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            const hours = Math.floor(
              (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
            );
            const minutes = Math.floor(
              (timeLeft % (1000 * 60 * 60)) / (1000 * 60),
            );
            const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

            newCountdowns[item.id] = { days, hours, minutes, seconds };
          } else {
            newCountdowns[item.id] = {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
            };
          }
        }
      });

      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(timer);
  }, [content, type]);

  const startResult = useMemo(
    () => (total > 0 ? (currentPageData - 1) * limit + 1 : 0),
    [total, currentPageData, limit],
  );
  const endResult = useMemo(
    () => (total > 0 ? Math.min(currentPageData * limit, total) : 0),
    [total, currentPageData, limit],
  );
  const hasDisplayedContent = displayedContent.length > 0;

  // Handler pagination dibagi supaya klik nomor halaman tidak ikut mereset page
  const handlePageChange = (page: number, pageSize: number) => {
    if (pageSize !== limit) return;
    onPaginationChange(page, pageSize);
  };

  const handlePageSizeChange = (_current: number, size: number) => {
    onPaginationChange(1, size);
  };

  // --- Fungsi untuk membuat link dengan filter ---
  const createFilterLink = (categoryId?: string, level?: string) => {
    if (!type) return "#";

    const baseRoutes = {
      Course: all_routes.jadiCourse,
      Live_Teaching: all_routes.liveTeaching,
      CBT: all_routes.jadiUkom,
    };

    const categoryRoutes = {
      Course: all_routes.jadiCourseWithCategory,
      Live_Teaching: all_routes.liveTeachingWithCategory,
      CBT: all_routes.jadiUkomWithCategory,
    };

    if (categoryId) {
      const categorySlug = getCategorySlug(categoryId);
      if (categorySlug) {
        return (
          categoryRoutes[type as keyof typeof categoryRoutes]?.replace(
            ":category",
            categorySlug,
          ) || "#"
        );
      }
    }

    return baseRoutes[type as keyof typeof baseRoutes] || "#";
  };

  // --- Handler untuk navigasi dengan filter ---
  const handleCategoryFilter = (categoryId: string, checked: boolean) => {
    if (checked) {
      const link = createFilterLink(categoryId);
      navigate(link);
    } else {
      // Jika uncheck, kembali ke halaman utama
      const baseRoute =
        type === "Course"
          ? all_routes.jadiCourse
          : type === "Live_Teaching"
            ? all_routes.liveTeaching
            : all_routes.jadiUkom;
      navigate(baseRoute);
    }
  };

  // --- Fungsi untuk mendapatkan breadcrumb title ---
  const getBreadcrumbTitle = () => {
    if (selectedCategories.length > 0 && dataCategories) {
      const category = dataCategories.find(
        (cat: any) => cat.id?.toString() === selectedCategories[0],
      );
      if (category) {
        return `${
          type === "Course"
            ? "Courses"
            : type === "Live_Teaching"
              ? "Live Teaching"
              : "CBT"
        } - ${category.name}`;
      }
    }

    if (selectedLevels.length > 0) {
      const level = CourseLevel.find(
        (level) => level.value === selectedLevels[0],
      );
      if (level) {
        return `${level.label} Level ${
          type === "Course"
            ? "Courses"
            : type === "Live_Teaching"
              ? "Live Teaching"
              : "CBT"
        }`;
      }
    }

    return type === "Course"
      ? "E-Courses"
      : type === "Live_Teaching"
        ? "Live Teaching"
        : "CBT";
  };

  //fungsi untuk merender count category
  const renderCountCategory = (category: any) => {
    switch (type) {
      case "Course":
        return category?.course_count;
      case "Live_Teaching":
        return category?.live_teaching_count;
      case "CBT":
        return category?.cbt_count;
      default:
        return 0;
    }
  };

  // --- Fungsi untuk mendapatkan detail route sesuai type ---
  const getDetailRoute = (type?: string, id?: string) => {
    let route = "";
    switch (type) {
      case "Course":
        route = all_routes.jadiCourseDetail;
        break;
      case "Live_Teaching":
        route = all_routes.liveTeachingDetail;
        break;
      case "CBT":
        route = all_routes.jadiUkomDetail;
        break;
      default:
        route = all_routes.jadiCourseDetail;
    }
    return route.replace(":id", id || "");
  };

  return (
    <>

      {/* Course */}
      <section className="course-content pt-4">
        <div className="container">
          {/* Header Katalog Baru */}
          <div className="bg-white rounded-4 shadow-sm p-3 p-md-4 mb-4 border" style={{ borderColor: "#f1f5f9" }}>
            {/* Title Katalog */}
            <h3 className="fw-bold mb-3 mb-md-4" style={{ color: "#1d7092", fontSize: "24px" }}>
              {type === "Course"
                ? "Katalog E-Course"
                : type === "Live_Teaching"
                  ? "Katalog Live Teaching"
                  : type === "CBT"
                    ? "Katalog Tryout CBT"
                    : "Katalog Kursus"}
            </h3>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              {/* Tab Pills: Semua | Terfavorit */}
              <div
                className="d-inline-flex align-items-center p-1 bg-white"
                style={{ borderRadius: "50px", border: "1px solid #e2e8f0" }}
              >
                <button
                  type="button"
                  className="btn btn-sm rounded-pill fw-bold px-3 px-sm-4 py-2 border-0 shadow-none"
                  style={{
                    backgroundColor: !isFavorit ? "#1d7092" : "transparent",
                    color: !isFavorit ? "#ffffff" : "#64748b",
                    borderRadius: "50px",
                    fontSize: "14px",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => handleSetBestSeller(false)}
                >
                  Semua
                </button>
                <button
                  type="button"
                  className="btn btn-sm rounded-pill fw-bold px-3 px-sm-4 py-2 border-0 shadow-none"
                  style={{
                    backgroundColor: isFavorit ? "#1d7092" : "transparent",
                    color: isFavorit ? "#ffffff" : "#64748b",
                    borderRadius: "50px",
                    fontSize: "14px",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => handleSetBestSeller(true)}
                >
                  Terfavorit
                </button>
              </div>

              {/* Controls Kanan: Search & Kategori Dropdown */}
              <div className="d-flex align-items-center gap-2 gap-sm-3 ms-auto flex-wrap">
                {/* Search Bar */}
                <div
                  className="d-flex align-items-center bg-white overflow-hidden border shadow-sm"
                  style={{
                    borderRadius: "10px",
                    borderColor: "#cbd5e1",
                    height: "42px",
                    width: "250px",
                  }}
                >
                  <input
                    type="text"
                    className="form-control border-0 shadow-none px-3 text-secondary"
                    placeholder={
                      type === "Course"
                        ? "Cari E-Course"
                        : type === "Live_Teaching"
                          ? "Cari Live Teaching"
                          : type === "CBT"
                            ? "Cari Tryout CBT"
                            : "Cari..."
                    }
                    value={searchInputVal}
                    onChange={(e) => {
                      setSearchInputVal(e.target.value);
                      handleSearchInputChange(e);
                    }}
                    style={{ fontSize: "14px", background: "transparent" }}
                  />
                  <button
                    className="btn text-white px-3 border-0 d-flex align-items-center justify-content-center h-100 shadow-none"
                    style={{
                      backgroundColor: "#1d7092",
                      borderRadius: "0 10px 10px 0",
                      minWidth: "46px",
                    }}
                    type="button"
                  >
                    <i className="isax isax-search-normal-1 fw-bold fs-16" />
                  </button>
                </div>

                {/* Dropdown Kategori (Tersusun Rapi tanpa Double Arrow) */}
                <div
                  className="d-inline-flex align-items-center bg-white border shadow-sm position-relative"
                  style={{
                    borderRadius: "10px",
                    borderColor: "#cbd5e1",
                    height: "42px",
                    width: "200px",
                    maxWidth: "220px",
                  }}
                >
                  <select
                    className="form-select border-0 shadow-none ps-3 bg-transparent h-100 w-100"
                    value={selectedCategories[0] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        handleCategoryFilter(val, true);
                      } else {
                        const baseRoute =
                          type === "Course"
                            ? all_routes.jadiCourse
                            : type === "Live_Teaching"
                              ? all_routes.liveTeaching
                              : all_routes.jadiUkom;
                        navigate(baseRoute);
                      }
                    }}
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      backgroundImage: "none",
                      cursor: "pointer",
                      zIndex: 2,
                      paddingRight: "46px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <option value="" style={{ color: "#64748b" }}>
                      Kategori
                    </option>
                    {dataCategories?.map((category: any) => (
                      <option key={category.id} value={category.id} style={{ color: "#334155" }}>
                        {category.name} ({renderCountCategory(category) || 0})
                      </option>
                    ))}
                  </select>
                  <div
                    className="position-absolute end-0 top-0 bottom-0 text-white d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "#1d7092",
                      width: "40px",
                      zIndex: 3,
                      pointerEvents: "none",
                      borderRadius: "0 10px 10px 0",
                    }}
                  >
                    <i className="isax isax-arrow-down-1 fw-bold fs-16" />
                  </div>
                </div>

                {/* Filter Waktu : [ Terdekat | Terlama ] */}
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold" style={{ fontSize: "14px", color: "#1d7092" }}>
                    Waktu :
                  </span>
                  <div
                    className="d-inline-flex align-items-center p-1 bg-white overflow-hidden border shadow-sm"
                    style={{
                      borderRadius: "10px",
                      borderColor: "#cbd5e1",
                      height: "42px",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm fw-bold px-3 py-1 border-0 shadow-none"
                      style={{
                        backgroundColor: (sortWaktu === "terdekat" || !sortWaktu) ? "#1d7092" : "transparent",
                        color: (sortWaktu === "terdekat" || !sortWaktu) ? "#ffffff" : "#475569",
                        borderRadius: "7px",
                        fontSize: "13px",
                        height: "100%",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => handleSetSortWaktu("terdekat")}
                    >
                      {type === "Live_Teaching" || type === "live_teaching" ? "Terdekat" : "Terbaru"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm fw-bold px-3 py-1 border-0 shadow-none"
                      style={{
                        backgroundColor: sortWaktu === "terlama" ? "#1d7092" : "transparent",
                        color: sortWaktu === "terlama" ? "#ffffff" : "#475569",
                        borderRadius: "7px",
                        fontSize: "13px",
                        height: "100%",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => handleSetSortWaktu("terlama")}
                    >
                      Terlama
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              {/* Filter & Search Result Summary */}
              {hasDisplayedContent && (
                <div className="showing-list mb-4">
                  <div className="row align-items-center">
                    <div className="col-12">
                      <div className="show-result text-center text-lg-start">
                        <h6 className="fw-medium text-muted">
                          Showing {startResult} - {endResult} of {total} results
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* /Filter */}

              {/* Course List */}
              {(isLoading || isRefetching) && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading content...</p>
                </div>
              )}

              {isError && (
                <div className="text-center py-5 text-danger">
                  <p>
                    Error loading content: {error?.message || "Unknown error"}
                  </p>
                  <p>Please try again later.</p>
                </div>
              )}

              {!isLoading &&
                !isError &&
                !isRefetching &&
                displayedContent.length === 0 && (
                  <div className="text-center py-5">
                    <p>
                      {isFavorit
                        ? "Belum ada kelas terfavorit yang ditampilkan."
                        : "Tidak ada konten yang sesuai dengan pencarian Anda."}
                    </p>
                  </div>
                )}

              <div className="row">
                {!isLoading &&
                  !isError &&
                  !isRefetching &&
                  displayedContent.map((item: any) => (
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-4" key={item.id}>
                        <div
                          className={`course-item-two course-item mx-0${type === 'Live_Teaching' ? ' live-card' : ''}`}
                          style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div className={`position-relative ${type === 'Live_Teaching' ? '' : 'course-img'}`}>
                            <Link to={getDetailRoute(type, item.id)}>
                              <ImageWithBasePath
                                src={""}
                                srcApi={
                                  item?.image || item?.poster || "/assets/img/course/course-48.jpg"
                                }
                                fallbackSrc="/assets/img/course/course-48.jpg"
                                alt={item?.title || 'img'}
                                className="img-fluid"
                                isLandscape={false}
                                objectFit={type === 'Live_Teaching' ? 'contain' : undefined}
                                style={type === 'Live_Teaching' ? { borderRadius: '20px' } : undefined}
                              />
                            </Link>
                            {isItemFavorite(item) && (
                              <div
                                className="position-absolute"
                                style={{
                                  top: '-2px',
                                  left: '-2px',
                                  zIndex: 15,
                                  pointerEvents: 'none',
                                }}
                              >
                                <div className="d-inline-flex flex-column align-items-start">
                                  <span
                                    className="badge text-white fw-bold d-inline-flex align-items-center gap-1.5 shadow"
                                    style={{
                                      background: "linear-gradient(135deg, #48bb78 0%, #276749 100%)",
                                      borderRadius: "8px 22px 22px 0px",
                                      fontSize: "12.5px",
                                      fontWeight: 800,
                                      padding: "6px 16px 6px 12px",
                                      boxShadow: "0 4px 12px rgba(39, 103, 73, 0.45)",
                                      letterSpacing: "0.3px",
                                      color: "#ffffff",
                                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                    }}
                                  >
                                    <i className="fa-solid fa-crown text-white me-1 fs-12" style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))" }} />
                                    <span>Best Seller</span>
                                  </span>
                                  {/* 3D Ribbon Fold Triangle */}
                                  <div
                                    style={{
                                      width: 0,
                                      height: 0,
                                      borderTop: "5px solid #1c4532",
                                      borderLeft: "5px solid transparent",
                                      marginTop: "-1px",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          <div
                            className="course-content"
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                {item.start_date &&
                                  item.end_date &&
                                  type === "Live_Teaching" && (
                                    <>
                                      {(() => {
                                        const now = new Date().getTime();
                                        const startDate = new Date(
                                          item.start_date,
                                        ).getTime();
                                        const endDate = new Date(
                                          item.end_date,
                                        ).getTime();

                                        if (now > endDate) {
                                          return (
                                            <span className="badge mb-0 bg-info">
                                              Event Selesai
                                            </span>
                                          );
                                        }

                                        if (
                                          now >= startDate &&
                                          now <= endDate
                                        ) {
                                          return (
                                            <span className="badge mb-0 bg-success">
                                              Live Now
                                            </span>
                                          );
                                        }

                                        if (now < startDate) {
                                          const itemCountdown = countdowns[
                                            item.id
                                          ] || {
                                            days: 0,
                                            hours: 0,
                                            minutes: 0,
                                            seconds: 0,
                                          };

                                          // Tampilkan format yang berbeda berdasarkan jumlah hari
                                          let countdownText = "";
                                          if (itemCountdown.days > 0) {
                                            countdownText = `${
                                              itemCountdown.days
                                            } hari ${itemCountdown.hours
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.minutes
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.seconds
                                              .toString()
                                              .padStart(2, "0")}`;
                                          } else {
                                            countdownText = `${itemCountdown.hours
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.minutes
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.seconds
                                              .toString()
                                              .padStart(2, "0")}`;
                                          }

                                          return (
                                            <span className="badge mb-0 bg-warning">
                                              {countdownText}
                                            </span>
                                          );
                                        }
                                      })()}
                                    </>
                                  )}
                                {type === "Course" && (
                                  <>
                                    <span className="badge mb-0 bg-info">
                                      {`${item.student_count || 0} Siswa`}
                                    </span>
                                  </>
                                )}
                                {type === "CBT" && (
                                  <>
                                    <span className="badge mb-0 bg-info">
                                      {`${item.questions_count || 0} Soal`}
                                    </span>
                                  </>
                                )}
                              </div>
                              <span className="badge badge-light rounded-pill bg-light d-inline-flex align-items-center fs-13 fw-medium mb-0">
                                {
                                  dataCategories?.find(
                                    (value: any) =>
                                      value?.id?.toString() ===
                                      item?.category_id,
                                  )?.name
                                }
                              </span>
                            </div>
                            <h6
                              className="title mb-2"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: "1.4",
                                minHeight: "2.8em", // 2 baris x 1.4 line-height
                                maxHeight: "2.8em",
                              }}
                            >
                              <Link to={getDetailRoute(type, item.id)}>
                                {item.title}
                              </Link>
                            </h6>
                            <div className="d-flex align-items-center">
                              <div className="price">
                                {!!item?.discount_type ? (
                                  <h5>
                                    {calculateDiscount(
                                      item?.price,
                                      item?.discount,
                                      item?.discount_type,
                                    )}{" "}
                                    <span>{rupiahFormatter(item?.price)}</span>
                                  </h5>
                                ) : (
                                  <h5>{rupiahFormatter(item?.price)} </h5>
                                )}
                              </div>
                            </div>
                            <Link
                              to={getDetailRoute(type, item.id)}
                              className="btn btn-dark btn-sm d-inline-flex align-items-center mt-2"
                              style={{
                                width: "fit-content",
                              }}
                            >
                              {type === 'Live_Teaching' ? 'Lihat Kelas' : 'Lihat Detail'}
                              {type !== 'Live_Teaching' && (
                                <i className="isax isax-arrow-right-3 ms-1" />
                              )}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
              </div>
              {/* /Course List */}

              {/* Pagination */}
              {!isLoading &&
                !isError &&
                !isRefetching &&
                hasDisplayedContent && (
                  <Pagination
                    align="end"
                    current={currentPageData}
                    total={total}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                    pageSize={limit}
                    showSizeChanger
                    pageSizeOptions={["12", "24", "48", "96"]}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageSizeChange}
                  />
                )}
              {/* /pagination */}
            </div>
          </div>
        </div>
      </section>
      {/* /Course */}
    </>
  );
};

export default CourseGrid;
